// Surveillance → Views: a photograph of what each camera sees, campus by campus.
//
// The caption under each image is the view's OWN name, stored with the photo
// when it was taken — not the camera's current name. A camera has no name in
// stock, loses it when sent for service, and gets a new one when reinstalled,
// so reading the live name would blank out or rewrite the caption on old
// photographs. The view is permanent; the camera's name is not.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import AuthImg from '../components/AuthImg';

export default function Views({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);   // every view, or the current one per camera
  const [lightbox, setLightbox] = useState(null);
  const [upload, setUpload] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/surveillance/views', { params: q ? { search: q } : {} }).then(r => r.data).catch(() => []),
      api.get('/surveillance/views/pending').then(r => r.data).catch(() => []),
    ]).then(([v, p]) => { setRows(v); setPending(p); }).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const shown = showAll ? rows : rows.filter(r => r.is_latest);
  const byCampus = {};
  shown.forEach(r => { const k = r.campus_name || '—'; (byCampus[k] = byCampus[k] || []).push(r); });

  const del = async (v) => {
    if (!window.confirm(`Delete the view "${v.view_name}"? The photograph is removed permanently.`)) return;
    await api.delete(`/surveillance/views/${v.id}`).catch(() => {});
    load();
  };

  return (
    <div>
      <div className="flex-gap" style={{ gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'end' }}>
        <div style={{ flex:1, minWidth:180 }}>
          <label style={{ fontSize:11, color:'var(--text2)' }}>Search view, location or camera number</label>
          <input value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <label className="opt"><input type="checkbox" checked={showAll}
          onChange={e => setShowAll(e.target.checked)} /><span>Include earlier views</span></label>
        {canEdit && <button className="btn btn-primary" onClick={() => setUpload({})}>+ Upload a view</button>}
      </div>

      {/* The outstanding list is the one worth acting on. Cameras in stock or
          at service are not counted — there is no installed view to take. */}
      {pending.length > 0 && (
        <div className="card" style={{ marginBottom:14, padding:12, borderColor:'var(--red)' }}>
          <b style={{ fontSize:13 }}>{pending.length} installed camera(s) with no view yet</b>
          <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:6, display:'flex', flexWrap:'wrap', gap:8 }}>
            {pending.slice(0, 24).map(p => (
              <button key={p.asset_id} className="btn btn-secondary btn-sm" disabled={!canEdit}
                onClick={() => setUpload({ asset_id: p.asset_id, view_name: p.camera_name || '' })}>
                {p.camera_name || p.asset_tag}
                <span style={{ color:'var(--text2)' }}> · {p.campus_name}</span>
              </button>
            ))}
            {pending.length > 24 && <span>and {pending.length - 24} more…</span>}
          </div>
        </div>
      )}

      {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
      : !shown.length ? <div className="empty-state"><p>No camera views uploaded yet.</p></div>
      : Object.entries(byCampus).map(([campus, list]) => (
        <div key={campus} style={{ marginBottom:22 }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:8, paddingBottom:4,
                        borderBottom:'1px solid var(--border)' }}>
            {campus} <span style={{ fontWeight:400, color:'var(--text2)' }}>· {list.length} view(s)</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(190px, 1fr))', gap:14 }}>
            {list.map(v => (
              <div key={v.id} className="card" style={{ padding:8 }}>
                <button type="button" onClick={() => setLightbox(v)}
                  style={{ display:'block', width:'100%', padding:0, border:'none', background:'none', cursor:'zoom-in' }}>
                  <AuthImg src={`/surveillance/views/${v.id}/file?size=thumb`} alt={v.view_name}
                    style={{ width:'100%', aspectRatio:'4 / 3', objectFit:'cover',
                             borderRadius:6, background:'var(--bg3)', display:'block' }} />
                </button>
                {/* The name under the image: the snapshot, not the live name. */}
                <div style={{ fontSize:12.5, fontWeight:600, marginTop:6, lineHeight:1.3 }}>{v.view_name}</div>
                <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>
                  {[v.camera_no, v.room_name, v.location_txt].filter(Boolean).join(' · ') || v.asset_tag}
                </div>
                {/* Where that camera is now, when it differs from the view. */}
                {v.current_camera_name !== v.view_name && (
                  <div style={{ fontSize:10.5, color:'var(--text3)', marginTop:2 }}>
                    camera now: {v.current_camera_name
                      || (v.asset_status === 'sent_for_service' ? 'at service'
                        : v.asset_status === 'stock' ? 'in stock' : 'unnamed')}
                  </div>
                )}
                {canEdit && (
                  <div className="flex-gap" style={{ gap:6, marginTop:6 }}>
                    <button className="btn btn-secondary" style={{ padding:'2px 7px', fontSize:10.5 }}
                      onClick={() => setUpload({ asset_id: v.asset_id, view_name: v.view_name })}>new photo</button>
                    <button className="btn btn-secondary" style={{ padding:'2px 7px', fontSize:10.5 }}
                      onClick={() => del(v)}>delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {lightbox && (
        <div className="modal-backdrop" onClick={() => setLightbox(null)}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth:'92vw', maxHeight:'92vh' }}>
            <AuthImg src={`/surveillance/views/${lightbox.id}/file`} alt={lightbox.view_name}
              style={{ maxWidth:'92vw', maxHeight:'80vh', objectFit:'contain', borderRadius:8, display:'block' }} />
            <div style={{ color:'#fff', textAlign:'center', marginTop:8, fontSize:14 }}>
              <b>{lightbox.view_name}</b>
              <div style={{ fontSize:12, opacity:0.8 }}>
                {[lightbox.campus_name, lightbox.camera_no, lightbox.location_txt].filter(Boolean).join(' · ')}
                {lightbox.taken_on ? ` · taken ${new Date(lightbox.taken_on).toLocaleDateString('en-IN')}` : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {upload && <UploadView prefill={upload} onClose={() => setUpload(null)}
                             onDone={() => { setUpload(null); load(); }} />}
    </div>
  );
}

function UploadView({ prefill, onClose, onDone }) {
  const [cams, setCams] = useState([]);
  const [f, setF] = useState({ asset_id: prefill.asset_id || '', view_name: prefill.view_name || '',
                               taken_on: '', notes: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/surveillance/devices', { params: { kind: 'camera' } })
       .then(r => setCams(r.data)).catch(() => {});
  }, []);

  // Choosing a camera offers its current name as a starting point. What gets
  // stored is whatever is in the box when you save — never a live link.
  const pick = (id) => {
    const c = cams.find(x => String(x.id) === String(id));
    setF(s => ({ ...s, asset_id: id, view_name: s.view_name || c?.camera_name || '' }));
  };

  const save = async () => {
    if (!file) { setErr('Choose an image'); return; }
    setBusy(true); setErr('');
    try {
      const data = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result));
        r.onerror = () => rej(new Error('Could not read that file'));
        r.readAsDataURL(file);
      });
      await api.post('/surveillance/views', { ...f, orig_name: file.name, mime: file.type, data });
      onDone();
    } catch (e) { setErr(e.response?.data?.error || e.message || 'Upload failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop:0 }}>Upload a camera view</h3>
        <div className="form-group"><label>Camera *</label>
          <select value={f.asset_id} onChange={e => pick(e.target.value)}>
            <option value="">— select —</option>
            {cams.map(c => (
              <option key={c.id} value={c.id}>
                {c.camera_name || '(unnamed)'} · {c.campus_name}
                {c.camera_number ? ` · ${c.camera_number}` : ''} · {c.asset_tag}
              </option>
            ))}
          </select></div>
        <div className="form-group"><label>View name *</label>
          <input value={f.view_name} onChange={e => setF(s => ({ ...s, view_name: e.target.value }))}
                 placeholder="e.g. Main Gate — entry side" />
          <span style={{ fontSize:11, color:'var(--text2)' }}>
            Stays with the photograph permanently, even if the camera is renamed, serviced or moved.
          </span></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div className="form-group" style={{ margin:0 }}><label>Taken on</label>
            <input type="date" value={f.taken_on}
                   onChange={e => setF(s => ({ ...s, taken_on: e.target.value }))} /></div>
          <div className="form-group" style={{ margin:0 }}><label>Image *</label>
            <input type="file" accept="image/png,image/jpeg,image/webp"
                   onChange={e => setFile(e.target.files[0] || null)} /></div>
        </div>
        <div className="form-group"><label>Notes</label>
          <input value={f.notes} onChange={e => setF(s => ({ ...s, notes: e.target.value }))} /></div>
        {err && <div style={{ color:'var(--red)', fontSize:13, marginBottom:8 }}>{err}</div>}
        <div className="flex-gap" style={{ gap:8, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}
                  disabled={busy || !f.asset_id || !f.view_name || !file}>
            {busy ? 'Uploading…' : 'Upload'}</button>
        </div>
      </div>
    </div>
  );
}
